import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth, ROLES, getRoleDisplayName } from '../context/AuthContext'
import {
  Package, LogOut, User, Warehouse, ClipboardList, Settings,
  AlertCircle, Ruler, Boxes, ShoppingCart, Shield, ChevronDown, Grid, Link2, Search, Send, MapPin,
  BoxSelect, Tag, Building2, BookOpen, Crown, Receipt, FileText, DollarSign, Layers, ArrowRight
} from 'lucide-react'
import { getPendingCount } from '../api/dimensionRequests'
import NotificationBell from './NotificationBell'
import AccountSwitcher from './AccountSwitcher'

// Dropdown component for navigation
function NavDropdown({ label, icon: Icon, items, isActive, badge }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
          isActive
            ? 'bg-blue-100 text-blue-700'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`}
      >
        <Icon className="h-4 w-4" />
        <span>{label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {items.map((item, index) => {
            if (item.show === false) return null
            const linkClass = `flex items-center space-x-2 px-4 py-2 text-sm ${
              item.isActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`
            const content = (
              <>
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </>
            )
            // Support external links via href
            if (item.href) {
              return (
                <a
                  key={index}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className={linkClass}
                >
                  {content}
                </a>
              )
            }
            return (
              <Link
                key={index}
                to={item.to}
                onClick={() => setIsOpen(false)}
                className={linkClass}
              >
                {content}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Simple nav link
function NavLink({ to, icon: Icon, label, isActive, badge, className = '' }) {
  return (
    <Link
      to={to}
      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium relative ${
        isActive
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
      } ${className}`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </Link>
  )
}

export default function Layout({ children }) {
  const { user, logout, isAdmin, isAdminOrCoordinator, canApproveDimensions, isInternalUser, isWarehouseAccount, isClientAccount, isSuperadmin, isImpersonating, stopImpersonating } = useAuth()

  // Get pending dimension requests count for badge (only for admin/manager)
  const { data: pendingCount } = useQuery({
    queryKey: ['dimension-requests-pending-count'],
    queryFn: getPendingCount,
    enabled: canApproveDimensions,
    refetchInterval: 45000
  })

  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isActive = (path) => location.pathname.startsWith(path)

  // Check if any item in warehouse group is active
  const isWarehouseActive = isActive('/warehouses') || isActive('/receipts') || isActive('/placements') || isActive('/stock') || isActive('/shipments')

  // Check if any item in service group is active
  const isServiceActive = isActive('/incidents') || isActive('/dimension-requests')

  // Check if any item in settings group is active
  const isSettingsActive = isActive('/settings') || isActive('/admin') || isActive('/admin/roles') || isActive('/account-links') || isActive('/catalog') || isActive('/legal-entities') || isActive('/help') || isActive('/superadmin')

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white text-center py-2 text-sm font-medium">
          Вы вошли как <strong>{user?.username}</strong> ({user?.account?.name})
          <button onClick={stopImpersonating} className="ml-3 underline hover:no-underline">
            Вернуться в свой аккаунт
          </button>
        </div>
      )}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center space-x-2">
                <Package className="h-8 w-8 text-blue-600" />
                <span className="text-xl font-bold text-gray-900">WMS</span>
              </Link>

              <div className="flex items-center space-x-1">
                {/* Товары - standalone */}
                <NavLink
                  to="/products"
                  icon={Package}
                  label="Товары"
                  isActive={isActive('/products')}
                />

                {/* Склад dropdown - for all account types */}
                <NavDropdown
                  label="Склад"
                  icon={Warehouse}
                  isActive={isWarehouseActive}
                  items={[
                    { to: '/warehouses', icon: Warehouse, label: 'Склады', isActive: isActive('/warehouses') },
                    { to: '/receipts', icon: ClipboardList, label: 'Приёмки', isActive: isActive('/receipts'), show: isWarehouseAccount },
                    { to: '/placements', icon: MapPin, label: 'Размещение', isActive: isActive('/placements'), show: isWarehouseAccount },
                    { to: '/shipments', icon: Send, label: 'Пополнение склада', isActive: isActive('/shipments'), show: isClientAccount },
                    { to: '/stock', icon: Boxes, label: 'Остатки', isActive: isActive('/stock') },
                    { to: '/stock/inventories', icon: ClipboardList, label: 'Инвентаризации', isActive: isActive('/stock/inventories'), show: isWarehouseAccount },
                    { to: '/stock/movement', icon: ArrowRight, label: 'Перемещение', isActive: isActive('/stock/movement'), show: isWarehouseAccount },
                    { to: '/labels/templates', icon: Tag, label: 'Шаблоны этикеток', isActive: isActive('/labels/templates'), show: isWarehouseAccount },
                    { href: 'https://ah.zionhub.ru', icon: MapPin, label: 'Ячейки', show: isWarehouseAccount },
                  ]}
                />

                {/* Заказы dropdown */}
                <NavDropdown
                  label="Заказы"
                  icon={ShoppingCart}
                  isActive={isActive('/orders') || isActive('/waves') || isActive('/assembly') || isActive('/packing') || isActive('/labeling')}
                  items={[
                    { to: '/orders', icon: ShoppingCart, label: 'Все заказы', isActive: isActive('/orders') },
                    { to: '/waves', icon: Layers, label: 'Волны сборки', isActive: isActive('/waves'), show: isWarehouseAccount },
                    { to: '/assembly', icon: BoxSelect, label: 'Сборка заказов', isActive: isActive('/assembly'), show: isWarehouseAccount },
                    { to: '/packing', icon: BoxSelect, label: 'Упаковка', isActive: isActive('/packing') && !isActive('/packing-stations'), show: isWarehouseAccount },
                    { to: '/packing-stations', icon: BoxSelect, label: 'Столы упаковки', isActive: isActive('/packing-stations'), show: isWarehouseAccount && isAdminOrCoordinator },
                    { to: '/labeling', icon: Tag, label: 'Маркировка', isActive: isActive('/labeling'), show: isWarehouseAccount },
                  ]}
                />

                {/* Биллинг dropdown */}
                <NavDropdown
                  label="Биллинг"
                  icon={DollarSign}
                  isActive={isActive('/billing')}
                  items={[
                    { to: '/billing/tariffs', icon: Receipt, label: 'Тарифы', isActive: isActive('/billing/tariffs'), show: isWarehouseAccount },
                    { to: '/billing/invoices', icon: FileText, label: isClientAccount ? 'Мои счета' : 'Счета', isActive: isActive('/billing/invoices') },
                  ]}
                />

                {/* Служебное dropdown - for internal users and client accounts */}
                {(isInternalUser || isClientAccount) && (
                  <NavDropdown
                    label="Служебное"
                    icon={AlertCircle}
                    isActive={isServiceActive}
                    badge={canApproveDimensions ? pendingCount : 0}
                    items={[
                      { to: '/incidents', icon: AlertCircle, label: 'Инциденты', isActive: isActive('/incidents') },
                      {
                        to: '/dimension-requests',
                        icon: Ruler,
                        label: 'Проверка ВГШ',
                        isActive: isActive('/dimension-requests'),
                        badge: pendingCount,
                        show: canApproveDimensions && isWarehouseAccount
                      },
                    ]}
                  />
                )}

                {/* Настройки dropdown - admin panel only for admins */}
                <NavDropdown
                  label="Настройки"
                  icon={Settings}
                  isActive={isSettingsActive}
                  items={[
                    { to: '/settings', icon: Settings, label: 'Настройки', isActive: isActive('/settings') },
                    { to: '/catalog', icon: Search, label: 'Каталог складов', isActive: isActive('/catalog'), show: isClientAccount },
                    { to: '/legal-entities', icon: Building2, label: 'Юр.лица', isActive: isActive('/legal-entities'), show: isClientAccount },
                    { to: '/account-links', icon: Link2, label: 'Партнёры', isActive: isActive('/account-links'), show: isAdmin },
                    { to: '/admin', icon: Shield, label: 'Пользователи', isActive: isActive('/admin') && !isActive('/admin/roles'), show: isAdmin },
                    { to: '/admin/roles', icon: Grid, label: 'Матрица ролей', isActive: isActive('/admin/roles'), show: isAdmin },
                    { to: '/superadmin', icon: Crown, label: 'Суперадмин', isActive: isActive('/superadmin'), show: isSuperadmin },
                    { to: '/help', icon: BookOpen, label: 'Документация', isActive: isActive('/help') },
                  ]}
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <NotificationBell />
              <AccountSwitcher />
              <div className="flex items-center space-x-2 text-gray-600">
                <User className="h-5 w-5" />
                <span className="text-sm">
                  {user?.username}
                  {user?.roles?.length > 0 && (
                    <span className="ml-1 text-gray-400">({getRoleDisplayName(user.roles)})</span>
                  )}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              >
                <LogOut className="h-4 w-4" />
                <span>Выйти</span>
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className={location.pathname === '/help' ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {children}
      </main>
    </div>
  )
}
